// Pick up a new deploy without the parent having to kill the app.
//
// Android (the Bazaar app, and every messenger's WebView) keeps the page alive in
// the background, so after a deploy a parent can run yesterday's code for days —
// the product owner reopened the Bazaar app and didn't see a fix (2026-10-29).
// index.html is served `no-store`, so the LIVE entry script's hashed name is always
// one fetch away: when it differs from the one this page booted with, a newer build
// is out. The reload only happens where nothing can be lost — the shell passes
// `canReload` (home, no sheet or modal open); elsewhere it waits until the parent
// is back on home. Dev / preview (no hashed `main-*.js`) never checks.
import { useEffect, useRef } from "react";

const ENTRY_RE = /\/assets\/main-[^"'/?#]+\.js/;
const RELOADED_KEY = "zoha_reloaded_for";

const CURRENT = (() => {
  try {
    const src = [...document.scripts].map(s => s.src).find(u => ENTRY_RE.test(u));
    return src ? new URL(src).pathname : null;
  } catch { return null; }
})();

async function liveEntry() {
  const r = await fetch(`/?_v=${Date.now().toString(36)}`, { cache: "no-store" });
  const m = (await r.text()).match(ENTRY_RE);
  return m ? m[0] : null;
}

function reloadFor(entry) {
  // Never loop: if the page we reload into STILL isn't `entry` (a stale edge, a
  // half-finished deploy), don't reload for the same build again this session.
  try {
    if (sessionStorage.getItem(RELOADED_KEY) === entry) return;
    sessionStorage.setItem(RELOADED_KEY, entry);
  } catch (_) {}
  window.location.reload();
}

export function useAutoUpdate(canReload) {
  const pending = useRef(null);          // the newer entry, once seen
  const canRef = useRef(canReload);
  canRef.current = canReload;

  // Back on a safe screen with a newer build waiting → take it now.
  useEffect(() => {
    if (canReload && pending.current) reloadFor(pending.current);
  }, [canReload]);

  useEffect(() => {
    if (!CURRENT) return;
    let last = 0;
    const check = async () => {
      if (document.visibilityState !== "visible" || Date.now() - last < 60_000) return;
      last = Date.now();
      try {
        const live = await liveEntry();
        if (live && live !== CURRENT) {
          pending.current = live;
          if (canRef.current) reloadFor(live);
        }
      } catch (_) { /* offline — try again next time */ }
    };
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    const id = setInterval(check, 15 * 60_000);
    return () => {
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
      clearInterval(id);
    };
  }, []);
}
